import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  closeAuth,
  setAuthView,
  setResetEmail,
  setVerificationCode,
  setForgotTempPassword,
} from '../../redux/slice/authModalSlice';
import { Images } from '../../utils/imageConstants';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  ChangePasswordCommand,
  ConfirmForgotPasswordCommand,
  GlobalSignOutCommand,
  InitiateAuthCommand,
} from '@aws-sdk/client-cognito-identity-provider';
import toast from 'react-hot-toast';
import { newPasswordSchema, type NewPasswordFormValues } from './validation/loginSchema';
import { clientRegion, generateSecretHash } from '../../helper/helper';

const NewPassword: React.FC = () => {
  const dispatch = useAppDispatch();
  const email = useAppSelector((s) => s.authModal.emailForReset);
  const verificationCode = useAppSelector((s) => s.authModal.verificationCode);
  const forgotTempPassword = useAppSelector((s) => s.authModal.forgotTempPassword);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<NewPasswordFormValues>({
    resolver: yupResolver(newPasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const onSubmit = async (values: NewPasswordFormValues) => {
    if (!email) {
      toast.error('Session expired. Please try again.');
      dispatch(setAuthView('forgot'));
      return;
    }

    setIsSubmitting(true);

    try {
      if (forgotTempPassword) {
        const authSecretHash = generateSecretHash(email);
        const authResponse = await clientRegion.send(
          new InitiateAuthCommand({
            AuthFlow: 'USER_PASSWORD_AUTH',
            ClientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
            AuthParameters: {
              USERNAME: email,
              PASSWORD: forgotTempPassword,
              SECRET_HASH: authSecretHash,
            },
          })
        );
        const accessToken = authResponse.AuthenticationResult?.AccessToken;
        if (!accessToken) {
          throw new Error('Could not start password reset session. Please request a new code.');
        }

        await clientRegion.send(
          new ChangePasswordCommand({
            AccessToken: accessToken,
            PreviousPassword: forgotTempPassword,
            ProposedPassword: values.password,
          })
        );

        try {
          await clientRegion.send(new GlobalSignOutCommand({ AccessToken: accessToken }));
        } catch {
          /* best-effort sign-out of temp session */
        }

        dispatch(setForgotTempPassword(undefined));
        toast.success('Password has been reset successfully!');
        dispatch(setResetEmail(undefined));
        dispatch(setVerificationCode(undefined));
        dispatch(closeAuth());
        dispatch(setAuthView('signin'));
        return;
      }

      if (verificationCode) {
        const secretHash = generateSecretHash(email);
        await clientRegion.send(
          new ConfirmForgotPasswordCommand({
            ClientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
            Username: email,
            ConfirmationCode: verificationCode,
            Password: values.password,
            SecretHash: secretHash,
          })
        );
        dispatch(setVerificationCode(undefined));
        toast.success('Password has been reset successfully!');
        dispatch(setResetEmail(undefined));
        dispatch(closeAuth());
        dispatch(setAuthView('signin'));
        return;
      }

      toast.error('Session expired. Please try again.');
      dispatch(setAuthView('forgot'));
    } catch (err: any) {
      console.error('Reset password error:', err);
      const errorMessage = err?.message || 'Failed to reset password';

      if (errorMessage.includes('CodeMismatchException') || err?.name === 'CodeMismatchException') {
        toast.error('Invalid verification code');
      } else if (errorMessage.includes('InvalidPasswordException') || err?.name === 'InvalidPasswordException') {
        toast.error('Password does not meet requirements');
      } else if (errorMessage.includes('NotAuthorizedException') || err?.name === 'NotAuthorizedException') {
        toast.error('Session expired. Please start forgot password again.');
        dispatch(setForgotTempPassword(undefined));
        dispatch(setAuthView('forgot'));
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <div className="figma-login-container">
      <div className="login-background-image">
        <div className="login-modal-container">
          <div className="login-header">
            <div className="logo-container">
              <img src={Images.main_logo} alt="CRICCENTER" className="logo" />
            </div>
            <h1 className="app-name">CRICCENTER</h1>
          </div>

          <h2 className="signin-title">Create New Password</h2>

          <form onSubmit={handleSubmit(onSubmit)} className="login-form">
            <div className="form-group">
              <label htmlFor="newPassword" className="form-label">
                New Password
              </label>
              <div className="input-wrapper">
                <input
                  id="newPassword"
                  type={showPassword ? "text" : "password"}
                  className={`form-input ${errors.password ? 'error' : ''}`}
                  placeholder="Enter New Password"
                  {...register('password')}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={togglePasswordVisibility}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor" />
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" fill="currentColor" />
                    </svg>
                  )}
                </button>
              </div>
              {errors.password && (
                <span className="error-message">{errors.password.message as string}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword" className="form-label">
                Confirm New Password
              </label>
              <div className="input-wrapper">
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
                  placeholder="Confirm New Password"
                  {...register('confirmPassword')}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={toggleConfirmPasswordVisibility}
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor" />
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" fill="currentColor" />
                    </svg>
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <span className="error-message">{errors.confirmPassword.message as string}</span>
              )}
            </div>

            <div className="action-buttons">
              <button
                type="submit"
                className="btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>

            <div className="enter-another-email-section">
              <button
                type="button"
                className="enter-another-email-button"
                onClick={() => {
                  dispatch(setForgotTempPassword(undefined));
                  dispatch(setVerificationCode(undefined));
                  dispatch(setAuthView('forgot'));
                }}
              >
                Enter Another Email
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NewPassword;
